import { useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import type { MyUsage } from "@/api/fair-use"

import { createMyUsageRequest } from "@/api/fair-use"
import { CheckCircle2, Hourglass } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"

import { useFairUseStore } from "../fair-use-store"

type RequestMoreDialogProps = {
  usage: MyUsage
}

type DialogPhase = "form" | "submitting" | "success" | "already_pending"

/**
 * Request-more budget dialog (spec section 7): optional motivation textarea,
 * pending/success states, and graceful handling of the 409 already-pending
 * answer. Open state lives in the fair-use store so the popover can hand over
 * to it (and a blocked flow can survive the popover closing).
 */
export function RequestMoreDialog({ usage }: RequestMoreDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const open = useFairUseStore((state) => state.requestDialogOpen)
  const setOpen = useFairUseStore((state) => state.setRequestDialogOpen)

  const [phase, setPhase] = useState<DialogPhase>("form")
  const [motivation, setMotivation] = useState("")
  const [submitError, setSubmitError] = useState(false)

  const alreadyPending = usage.request?.pending
  const effectivePhase: DialogPhase = phase === "form" && alreadyPending ? "already_pending" : phase

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      // Reset for the next visit; keep it out of the closing animation frame.
      window.setTimeout(() => {
        setPhase("form")
        setMotivation("")
        setSubmitError(false)
      }, 200)
    }
  }

  const handleSubmit = async () => {
    setPhase("submitting")
    setSubmitError(false)
    try {
      const result = await createMyUsageRequest(motivation)
      setPhase(result.outcome === "created" ? "success" : "already_pending")
      // Mark the pending request in the cache immediately, then refetch the
      // authoritative snapshot in the background.
      queryClient.setQueryData<MyUsage>(queryKeys.fairUseMyUsage, (old) =>
        old ? { ...old, request: { allowed: old.request?.allowed ?? false, pending: true } } : old
      )
      void queryClient.invalidateQueries({ queryKey: queryKeys.fairUseMyUsage })
    } catch (error) {
      logger.error(
        "Failed to create fair-use budget request",
        error instanceof Error ? error : new Error(String(error))
      )
      setPhase("form")
      setSubmitError(true)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="fair-use-request-dialog">
        {(effectivePhase === "form" || effectivePhase === "submitting") && (
          <>
            <DialogHeader>
              <DialogTitle>{t("fairUse.requestDialog.title")}</DialogTitle>
              <DialogDescription>{t("fairUse.requestDialog.description")}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Label htmlFor="fair-use-motivation">
                {t("fairUse.requestDialog.motivationLabel")}
              </Label>
              <Textarea
                data-testid="fair-use-motivation"
                disabled={effectivePhase === "submitting"}
                id="fair-use-motivation"
                placeholder={t("fairUse.requestDialog.motivationPlaceholder")}
                rows={3}
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
              />
            </div>
            {submitError && (
              <p className="text-destructive text-xs">{t("fairUse.requestDialog.submitError")}</p>
            )}
            <DialogFooter>
              <Button
                disabled={effectivePhase === "submitting"}
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                data-testid="fair-use-request-submit"
                disabled={effectivePhase === "submitting"}
                onClick={() => void handleSubmit()}
              >
                {effectivePhase === "submitting"
                  ? t("common.sending")
                  : t("fairUse.requestDialog.submit")}
              </Button>
            </DialogFooter>
          </>
        )}
        {effectivePhase === "success" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 aria-hidden="true" className="text-success size-5" />
                {t("fairUse.requestDialog.successTitle")}
              </DialogTitle>
              <DialogDescription>{t("fairUse.requestDialog.successDescription")}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => handleOpenChange(false)}>{t("common.close")}</Button>
            </DialogFooter>
          </>
        )}
        {effectivePhase === "already_pending" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Hourglass aria-hidden="true" className="text-muted-foreground size-5" />
                {t("fairUse.requestDialog.pendingTitle")}
              </DialogTitle>
              <DialogDescription>{t("fairUse.requestDialog.pendingDescription")}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => handleOpenChange(false)}>{t("common.close")}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
